import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";

import { AjaxResult } from "../../../../../enums/ajaxResult";
import { RequestState } from "../../../../../enums/requestState";
import { ApiResult } from "../../../../../rest/axiosApi";
import { RestartAck } from "../../../../../signalr/state";

/**
 * How long to wait for the site to acknowledge before giving up.
 *
 * These hub events carry no ServiceComponentType capability flag, so an ACS build that predates
 * them simply has no handler registered and drops the message silently. Without this timeout the
 * button would sit in "Restarting..." forever.
 */
const ACK_TIMEOUT_MS = 60_000;

const NO_ACK_MESSAGE =
    "No response from the site - it may be offline or running an older service version.";

interface Options {
    siteId: number;
    /** Fires the REST call that asks SSA to publish the restart event. */
    request: () => Promise<ApiResult<unknown>>;
    /** The matching acknowledgement from SignalR context, or undefined if none has arrived. */
    ack: RestartAck | undefined;
    /** Shown when the REST call itself fails. */
    requestErrorMessage: string;
    /** Shown when the site acks with success. */
    successMessage: string;
    /** Shown when the site acks with failure (e.g. no device connected). */
    failureMessage: string;
}

/**
 * Drives a Device Actions restart button: fire the request, wait for the site's SignalR
 * acknowledgement, and fall back to Idle if none arrives.
 */
export function useRestartAction({
    siteId,
    request,
    ack,
    requestErrorMessage,
    successMessage,
    failureMessage,
}: Options) {
    const { enqueueSnackbar } = useSnackbar();
    const [requestState, setRequestState] = useState<RequestState>(RequestState.Idle);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    /** Latest ack, readable from callbacks without making them depend on it. */
    const ackRef = useRef(ack);
    ackRef.current = ack;

    /**
     * The last ack this button has accounted for. Seeded when the request is fired so an ack
     * left over from an earlier restart is never mistaken for this one's.
     */
    const handledAckRef = useRef<RestartAck | undefined>(ack);

    const clearPendingTimeout = useCallback(() => {
        if (timeoutRef.current !== undefined) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
        }
    }, []);

    useEffect(() => clearPendingTimeout, [clearPendingTimeout]);

    const trigger = useCallback(() => {
        // Anything already sitting in context predates this request.
        handledAckRef.current = ackRef.current;
        setRequestState(RequestState.Requesting);

        request().then((r) => {
            if (r.result !== AjaxResult.Success) {
                setRequestState(RequestState.Idle);
                enqueueSnackbar(requestErrorMessage, { variant: "error" });
                return;
            }

            setRequestState(RequestState.Processing);

            clearPendingTimeout();
            timeoutRef.current = setTimeout(() => {
                setRequestState(RequestState.Idle);
                enqueueSnackbar(NO_ACK_MESSAGE, { variant: "warning" });
            }, ACK_TIMEOUT_MS);
        });
    }, [request, enqueueSnackbar, requestErrorMessage, clearPendingTimeout]);

    /**
     * Keyed on the request state as well as the ack. The ack travels over SignalR while the
     * REST response travels over HTTP, so a fast ack can beat a slow response and land while
     * this is still Requesting. Re-checking on the transition into Processing picks that ack up;
     * keying on `ack` alone would drop it, and since its identity never changes again the button
     * would hang until the timeout and then wrongly report the site never answered.
     *
     * handledAckRef is what keeps this firing once per ack despite the wider dependency list.
     */
    useEffect(() => {
        if (requestState !== RequestState.Processing) {
            return;
        }

        if (!ack || ack === handledAckRef.current) {
            return;
        }
        handledAckRef.current = ack;

        if (ack.siteId !== siteId) {
            return;
        }

        clearPendingTimeout();
        setRequestState(RequestState.Idle);
        enqueueSnackbar(ack.success ? successMessage : failureMessage, {
            variant: ack.success ? "success" : "error",
        });
    }, [ack, requestState, siteId, clearPendingTimeout, enqueueSnackbar, successMessage, failureMessage]);

    return { requestState, trigger };
}
